import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import dayjs from 'dayjs';
import { CircleAlert, CircleCheck } from 'lucide-react';
import sales from '../Sales';
import logo from '../Login/logotechdoggroupmetpayoff.png';
import { AdminContext } from './adminContext';
import LiveTv from './LiveTv';
import { clearStore, load, refreshAll } from './store';
import { Button, ConfirmDialog, Spinner } from './ui';
import './Admin.css';

const cookies = new Cookies();

const tabs = [
  { to: '/admin/deals', label: 'Deals' },
  { to: '/admin/verkopers', label: 'Verkopers' },
  { to: '/admin/diensten', label: 'Diensten' },
  { to: '/admin/targets', label: 'Targets' },
];

const toastMs = 3500;

const prefetchUrls = ['deals', 'users', 'products', 'bonuses', 'users?filter=true', 'products?filter=true'];

const dashboardPath = () => {
  const now = dayjs();
  return `/dashboard/${now.month() + 1}/${now.year()}`;
};

const tabClass = ({ isActive }) => `adm-tab${isActive ? ' is-active' : ''}`;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [session, setSession] = useState('checking');
  const [attempt, setAttempt] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const toastId = useRef(0);
  const tvRef = useRef(null);

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const logOut = useCallback(() => {
    cookies.remove('token', { path: '/' });
    clearStore();
    navigateRef.current('/', { replace: true });
  }, []);

  useEffect(() => {
    const token = cookies.get('token');
    if (!token) {
      navigateRef.current('/', { replace: true });
      return undefined;
    }

    let active = true;
    setSession('checking');
    (async () => {
      try {
        const res = await sales.get(`login?ApiKey=${token}`);
        if (!active) return;
        if (Number(res.data.admin) !== 1) {
          navigateRef.current(dashboardPath(), { replace: true });
          return;
        }
        setSession('ready');
        prefetchUrls.forEach((url) => load(url).catch(() => {}));
      } catch (error) {
        console.warn(error);
        if (!active) return;
        if (error.response) {
          logOut();
        } else {
          setSession('offline');
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [attempt, logOut]);

  useEffect(() => {
    const id = sales.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) logOut();
        return Promise.reject(error);
      },
    );
    return () => sales.interceptors.response.eject(id);
  }, [logOut]);

  const notify = useCallback((text, type = 'success') => {
    toastId.current += 1;
    const id = toastId.current;
    setToasts((list) => [...list, { id, text, type }]);
    setTimeout(() => setToasts((list) => list.filter((toast) => toast.id !== id)), toastMs);
  }, []);

  const confirm = useCallback(
    (options) => new Promise((resolve) => setConfirmState({ ...options, resolve })),
    [],
  );

  const closeConfirm = (answer) => {
    confirmState?.resolve(answer);
    setConfirmState(null);
  };

  const changed = useCallback(() => {
    refreshAll();
    tvRef.current?.refresh();
  }, []);

  const context = useMemo(() => ({ notify, confirm, changed }), [notify, confirm, changed]);

  if (session !== 'ready') {
    return (
      <div className="adm-boot">
        {session === 'offline' ? (
          <div className="adm-boot-card">
            <CircleAlert size={28} aria-hidden />
            <h1>Geen verbinding met de server</h1>
            <p>Controleer de internetverbinding en probeer het opnieuw.</p>
            <Button onClick={() => setAttempt((value) => value + 1)}>Opnieuw proberen</Button>
          </div>
        ) : (
          <Spinner />
        )}
      </div>
    );
  }

  return (
    <AdminContext.Provider value={context}>
      <div className="adm">
        <aside className="adm-tv">
          <LiveTv ref={tvRef} />
        </aside>

        <div className="adm-work">
          <header className="adm-top">
            <img className="adm-top-logo" src={logo} alt="TechDog Group" />
            <nav className="adm-tabs" aria-label="Beheer">
              {tabs.map((tab) => (
                <NavLink key={tab.to} to={tab.to} className={tabClass}>
                  {tab.label}
                </NavLink>
              ))}
            </nav>
            <div className="adm-top-links">
              <NavLink to="/admin/wachtwoorden" className={tabClass}>
                Wachtwoorden
              </NavLink>
              <button type="button" className="adm-tab" onClick={logOut}>
                Uitloggen
              </button>
            </div>
          </header>

          <main className="adm-main">
            <div key={location.pathname} className="adm-page">
              <Outlet />
            </div>
          </main>
        </div>

        <div className="adm-toasts" aria-live="polite">
          {toasts.map((toast) => (
            <div key={toast.id} className={`adm-toast is-${toast.type}`}>
              {toast.type === 'error' ? (
                <CircleAlert size={18} aria-hidden />
              ) : (
                <CircleCheck size={18} aria-hidden />
              )}
              <span>{toast.text}</span>
            </div>
          ))}
        </div>

        {confirmState && <ConfirmDialog {...confirmState} onClose={closeConfirm} />}
      </div>
    </AdminContext.Provider>
  );
};

export default AdminLayout;
